import React, { useState } from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useSettingsSelector from "../../store/selectors/settings";
import { VISIBLE_CITATIONS, citationsOf } from "./turnSlots";

const DocIcon = () => (
	<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true" focusable="false">
		<path d="M6 2h9l5 5v15H6z" />
		<path d="M9 12h7M9 16h7" strokeLinecap="round" />
	</svg>
);

/**
 * The reply's citations as chips at the end of the claim (spec-chat-experience D-47): two, then a `+N` pill
 * that unfolds the rest. Each title is cut to about fifteen characters; the full one is the chip's `title`.
 * A chip is a link to the source's public page, opened in a new tab (FR-135, bug 86bcmw87t).
 */
function CitationChips({ citations }) {
	const [expanded, setExpanded] = useState(false);
	const shown = expanded ? citations : citations.slice(0, VISIBLE_CITATIONS);
	const rest = citations.slice(VISIBLE_CITATIONS);
	return (
		<span className="citation-chips">
			{shown.map((c, idx) => (
				<a key={`${c.name}|${c.url}|${idx}`} className="citation-chip" href={c.url} target="_blank" rel="noopener noreferrer" title={c.name}>
					<DocIcon />
					<span className="citation-chip-label">{c.name}</span>
				</a>
			))}
			{!expanded && rest.length > 0 && (
				<button
					type="button"
					className="citation-chip citation-more"
					title={rest.map((c) => c.name).join(", ")}
					aria-label={rest.map((c) => c.name).join(", ")}
					aria-expanded={false}
					onClick={() => setExpanded(true)}
				>
					{`+${rest.length}`}
				</button>
			)}
		</span>
	);
}

/**
 * A text reply's box. Mounted as the turn's answer by `Receiver` (`slotted`), it stamps its two children —
 * the answer and, nested at its end, the citations (D-47). Inside a legacy rich type (`MessageAndButtons`, the
 * collectors) it is part of that type's result slot and stamps nothing. The trace is NOT here any more: slot 1
 * is mounted by `Receiver` for every reply type (§6 G5).
 */
const Message = ({ content, sx = {}, isAttr = false, slotted = false }) => {
	const { RECEIVER } = useSettingsSelector();
	// The agent's reply has no bubble and runs full width — the one transcript (owner 2026-10-10; D-49). Data-collector
	// attribute cards are form prompts, not the reply, so they keep theirs.
	const unstyled = !isAttr;
	const rawMessage = content?._streamText ?? content?.message ?? content?.config?.message ?? "";
	const isStreaming = content?.streaming === true && typeof content?._streamText === "string";
	// Runtime 08c: a live trace with no text yet stands in for the cursor-only bubble (the trace itself is
	// slot 1, mounted by Receiver).
	const traceOnly = isStreaming && !!content?._trace && content._trace.off === false && !rawMessage;
	const cursor = "▍";
	const message = isStreaming ? rawMessage.replace(/(\n?)$/, "$1" + cursor) : rawMessage;
	const citations = citationsOf(content);

	// Custom link renderer with class
	const renderLink = ({ href, children, ...props }) => {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="markdown-link"
				{...props}
			>
				{children}
			</a>
		);
	};

	if (traceOnly) return null;

	const markdown = (
		<ReactMarkdown
			remarkPlugins={[remarkGfm]}
			components={{
				a: renderLink,
				img: ({ src, alt }) => (
					<img
						src={src}
						alt={alt}
						style={{
							maxWidth: "100%",
							height: "auto",
							borderRadius: "8px",
							marginTop: "12px",
							display: "block",
						}}
					/>
				),
			}}
		>
			{String(message || "")}
		</ReactMarkdown>
	);
	const chips = citations.length > 0 ? <CitationChips citations={citations} /> : null;

	return (
		<Box
			className="markdown-message-box"
			data-turn-slot={slotted ? "answer" : undefined}
			sx={{
				margin: 0,
				backgroundColor: unstyled ? "transparent" : RECEIVER.BACKGROUND_COLOR,
				padding: unstyled ? 0 : "16px",
				borderTopRightRadius: unstyled ? 0 : "12px",
				borderBottomRightRadius: unstyled ? 0 : "12px",
				borderBottomLeftRadius: unstyled ? 0 : "12px",
				overflowWrap: "break-word",
				wordBreak: "break-word",
				width: isAttr ? "fit-content" : "auto",
				...sx,
			}}
		>
			{markdown}
			{chips && (slotted ? <div data-turn-slot="sources" className="sources-wrapper">{chips}</div> : <div className="sources-wrapper">{chips}</div>)}
		</Box>
	);
};

export default Message;
